/**
 * AdminContactFinderCard — discover vendor / professional phone numbers
 * via Google Places API and bulk-import them into the marketing contact
 * book for WhatsApp invites.
 *
 * Inputs:
 *   • Profession / business type (free text)
 *   • Cities (multi-select chips from existing service_cities)
 *   • Areas / localities (optional comma-separated free text)
 *
 * Output:
 *   • Per-business: name, phone, address, Google Maps link
 *   • One-click "Save selected to Contact Book" + "Send invite now" handoffs
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MagnifyingGlass, Phone, MapPin, FloppyDisk, ArrowSquareOut, Database, WhatsappLogo } from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminContactFinderCard = () => {
  const [mode, setMode] = useState('single'); // 'single' | 'auto'
  const [serviceCities, setServiceCities] = useState([]);
  const [profession, setProfession] = useState('');
  const [professionsBlob, setProfessionsBlob] = useState('');
  const [selectedCities, setSelectedCities] = useState([]);
  const [areas, setAreas] = useState('');
  const [refresh, setRefresh] = useState(false);
  const [alsoInvite, setAlsoInvite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [autoResult, setAutoResult] = useState(null);
  const [selected, setSelected] = useState({}); // phone -> contact obj
  const [saving, setSaving] = useState(false);

  // Pull the canonical service cities from admin settings so the chips match
  // the rest of the platform.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/admin/service-cities`, { withCredentials: true });
        setServiceCities(data?.cities || []);
      } catch {
        setServiceCities(['Chennai', 'Coimbatore', 'Madurai', 'Bangalore', 'Hyderabad']);
      }
    })();
  }, []);

  const toggleCity = (c) => {
    setSelectedCities((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);
  };

  const runSearch = async () => {
    if (!profession.trim()) { toast.error('Enter the profession you want to find'); return; }
    if (selectedCities.length === 0 && !areas.trim()) {
      toast.error('Pick at least one city or paste at least one area/locality');
      return;
    }
    setLoading(true);
    setResult(null);
    setSelected({});
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/contact-finder/search`, {
        profession: profession.trim(),
        cities: selectedCities,
        areas: areas.trim() || null,
        refresh,
      }, { withCredentials: true });
      setResult(data);
      if (!data?.aggregated?.length) {
        if (data?.already_in_book > 0) {
          toast.info(`Found ${data.already_in_book} business${data.already_in_book === 1 ? '' : 'es'} — all already in your Contact Book. Nothing new.`);
        } else {
          toast.warning('No phone-number-bearing businesses returned for that search.');
        }
      } else {
        const extra = data.already_in_book > 0
          ? ` (${data.already_in_book} skipped — already in book)`
          : '';
        toast.success(`Found ${data.total_contacts} NEW business${data.total_contacts === 1 ? '' : 'es'}${extra} · ${data.cached_pages} cached, ${data.live_pages} live.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const runAuto = async () => {
    if (!professionsBlob.trim()) { toast.error('Paste at least one profession keyword'); return; }
    if (selectedCities.length === 0 && !areas.trim()) {
      toast.error('Pick at least one city or paste at least one area/locality');
      return;
    }
    const profCount = professionsBlob.split(/[\n,;|\t]+/).map((s) => s.trim()).filter(Boolean).length;
    const areaCount = (areas.trim() ? areas.trim().split(/[\s,;|]+/).filter(Boolean).length : 0);
    const locCount = areaCount > 0 && selectedCities.length > 0
      ? areaCount * selectedCities.length
      : (areaCount || selectedCities.length);
    const pairs = profCount * locCount;
    const msg = `Auto-Run will fire up to ${pairs} Google Places call${pairs === 1 ? '' : 's'} (${profCount} profession${profCount === 1 ? '' : 's'} × ${locCount} localit${locCount === 1 ? 'y' : 'ies'}) and auto-save every result to the Contact Book.\n\n${alsoInvite ? '⚡ It will ALSO blast the pro_join_request WhatsApp invite to every saved phone.' : ''}\n\nContinue?`;
    if (!window.confirm(msg)) return;

    setLoading(true);
    setAutoResult(null);
    setResult(null);
    setSelected({});
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/contact-finder/auto-run`, {
        professions: professionsBlob,
        cities: selectedCities,
        areas: areas.trim() || null,
        refresh,
        also_invite: alsoInvite,
      }, { withCredentials: true });
      setAutoResult(data);
      const newC = data?.saved_new ?? 0;
      const upd = data?.updated_existing ?? 0;
      const inviteMsg = data?.invite_sent_ok != null
        ? ` · WhatsApp sent ${data.invite_sent_ok}, failed ${data.invite_failed}`
        : '';
      toast.success(
        `Auto-Run done · ${newC} new + ${upd} merged across ${data.pairs_total} search${data.pairs_total === 1 ? '' : 'es'} (${data.live_pages} live · ${data.cached_pages} cached)${inviteMsg}`,
        { duration: 12000 },
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Auto-Run failed');
    } finally {
      setLoading(false);
    }
  };

  const allSelected = useMemo(() => {
    if (!result?.aggregated?.length) return false;
    return result.aggregated.every((r) => selected[r.phone]);
  }, [result, selected]);

  const toggleAll = () => {
    if (!result?.aggregated?.length) return;
    if (allSelected) { setSelected({}); return; }
    const next = {};
    result.aggregated.forEach((r) => { next[r.phone] = r; });
    setSelected(next);
  };

  const toggleOne = (r) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[r.phone]) delete next[r.phone];
      else next[r.phone] = r;
      return next;
    });
  };

  const selectedList = useMemo(() => Object.values(selected), [selected]);

  const saveSelected = async () => {
    if (!selectedList.length) { toast.error('Tick at least one contact'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/contact-finder/save`, {
        contacts: selectedList,
        profession: profession.trim(),
        cities: selectedCities,
        areas: areas.trim() || null,
      }, { withCredentials: true });
      const newC = data?.saved_new ?? 0;
      const upd = data?.updated_existing ?? 0;
      let msg = `${newC} new contact${newC === 1 ? '' : 's'} added`;
      if (upd > 0) msg += `, ${upd} already in book (merged tags)`;
      toast.success(msg);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveAndInviteAll = async () => {
    if (!selectedList.length) { toast.error('Tick at least one contact'); return; }
    if (!window.confirm(
      `Send the BeeWeddy WhatsApp invite (template "pro_join_request") to all ${selectedList.length} selected contact${selectedList.length === 1 ? '' : 's'}?\n\nThey'll also be saved to your Contact Book under: ${profession.trim() || '(no profession set)'}.`
    )) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/contact-finder/save-and-invite`, {
        contacts: selectedList,
        profession: profession.trim(),
        cities: selectedCities,
        areas: areas.trim() || null,
      }, { withCredentials: true });
      const ok = data?.invite_sent_ok ?? 0;
      const fail = data?.invite_failed ?? 0;
      const newC = data?.saved_new ?? 0;
      const upd = data?.updated_existing ?? 0;
      if (fail === 0) {
        toast.success(
          `Sent ${ok} WhatsApp invite${ok === 1 ? '' : 's'} · ${newC} new + ${upd} merged in Contact Book.`,
          { duration: 8000 },
        );
      } else {
        toast.warning(
          `Sent ${ok}, failed ${fail}. Saved ${newC} new + ${upd} merged. Check History tab for errors.`,
          { duration: 10000 },
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally {
      setSaving(false);
    }
  };

  const copyPhones = () => {
    const list = selectedList.length ? selectedList : (result?.aggregated || []);
    if (!list.length) { toast.error('Nothing to copy'); return; }
    const text = list.map((r) => r.phone).join(' ');
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`Copied ${list.length} phone${list.length === 1 ? '' : 's'} to clipboard`);
    }, () => toast.error('Clipboard blocked'));
  };

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="admin-contact-finder-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <MagnifyingGlass size={20} weight="bold" className="text-brand-primary" />
          Contact Finder
        </CardTitle>
        <CardDescription>
          Search Google Maps for any profession in selected cities/areas. Pull phone numbers, then push them straight into your Marketing Contact Book to invite via WhatsApp.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Mode toggle */}
        <div className="flex border-b border-[#E8E1D5] -mt-1">
          {[
            { k: 'single', label: 'Single Search' },
            { k: 'auto', label: 'Auto-Run (Bulk + Save)' },
          ].map((m) => (
            <button
              key={m.k}
              type="button"
              onClick={() => setMode(m.k)}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${mode === m.k ? 'border-brand-primary text-brand-primary' : 'border-transparent text-text-secondary hover:text-text-primary'}`}
              data-testid={`cf-mode-${m.k}`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {/* Profession — single line OR multi-line depending on mode */}
        <div className="space-y-1">
          <Label className="text-xs font-bold text-text-secondary">
            Profession / Business Type *
            {mode === 'auto' && (
              <span className="ml-1 font-normal text-text-secondary">— paste many (comma or newline separated)</span>
            )}
          </Label>
          {mode === 'single' ? (
            <Input
              type="text"
              placeholder='e.g. "wedding photographer", "caterer", "birthday decorator"'
              value={profession}
              onChange={(e) => setProfession(e.target.value)}
              className="rounded-none border-[#E8E1D5] h-9"
              data-testid="cf-profession-input"
            />
          ) : (
            <Textarea
              placeholder={'wedding photographer\ncandid photographer\nmarriage photography\npre-wedding shoot'}
              value={professionsBlob}
              onChange={(e) => setProfessionsBlob(e.target.value)}
              rows={4}
              className="rounded-none border-[#E8E1D5]"
              data-testid="cf-professions-blob"
            />
          )}
        </div>

        {/* Cities */}
        <div className="space-y-1">
          <Label className="text-xs font-bold text-text-secondary">Cities ({selectedCities.length} selected)</Label>
          <div className="flex flex-wrap gap-1.5" data-testid="cf-cities-chips">
            {serviceCities.map((c) => {
              const active = selectedCities.includes(c);
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleCity(c)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-all ${active ? 'bg-brand-primary text-sand-50 border-brand-primary' : 'bg-white text-text-secondary border-[#E8E1D5] hover:border-brand-primary/40'}`}
                  data-testid={`cf-city-${c.toLowerCase()}`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </div>

        {/* Areas */}
        <div className="space-y-1">
          <Label className="text-xs font-bold text-text-secondary">Areas / Localities (optional)</Label>
          <Input
            type="text"
            placeholder={mode === 'auto'
              ? 'T.Nagar Velachery AnnaNagar Adyar  (space-separated)'
              : 'T. Nagar, Velachery, Anna Nagar'}
            value={areas}
            onChange={(e) => setAreas(e.target.value)}
            className="rounded-none border-[#E8E1D5] h-9"
            data-testid="cf-areas-input"
          />
          <p className="text-[10px] text-text-secondary">
            {mode === 'auto'
              ? <>Paste many areas separated by <strong>spaces</strong> — each profession will be cross-searched against every area. For multi-word areas like "T. Nagar", use commas instead.</>
              : <>When set, we search each <strong>area + city</strong> combination separately for finer-grained results. Comma / line-separated.</>}
          </p>
        </div>

        {/* Refresh checkbox + Search button (mode-aware) */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col gap-1.5 text-xs text-text-secondary">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={refresh}
                onChange={(e) => setRefresh(e.target.checked)}
                className="h-3.5 w-3.5 accent-brand-primary"
                data-testid="cf-refresh-checkbox"
              />
              Bypass 30-day cache (live Google call — costs more)
            </label>
            {mode === 'auto' && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={alsoInvite}
                  onChange={(e) => setAlsoInvite(e.target.checked)}
                  className="h-3.5 w-3.5 accent-green-600"
                  data-testid="cf-auto-invite-checkbox"
                />
                Also send WhatsApp <code className="px-1 bg-stone-100">pro_join_request</code> to every saved phone
              </label>
            )}
          </div>
          {mode === 'single' ? (
            <Button
              onClick={runSearch}
              disabled={loading}
              className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none"
              data-testid="cf-search-btn"
            >
              {loading ? 'Searching…' : 'Find Contacts'}
            </Button>
          ) : (
            <Button
              onClick={runAuto}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 text-white rounded-none"
              data-testid="cf-auto-run-btn"
            >
              {loading ? 'Running…' : 'Auto-Run & Save All'}
            </Button>
          )}
        </div>

        {/* Auto-Run results summary */}
        {autoResult && (
          <div className="border-t border-[#E8E1D5] pt-4 space-y-2" data-testid="cf-auto-results">
            <div className="p-3 border border-green-300 bg-green-50 rounded text-sm">
              <div className="font-bold text-green-900">Auto-Run complete</div>
              <div className="text-xs text-green-900 mt-1">
                <strong>{autoResult.saved_new}</strong> new + <strong>{autoResult.updated_existing}</strong> merged ·
                {' '}{autoResult.pairs_total} search{autoResult.pairs_total === 1 ? '' : 'es'} ·
                {' '}{autoResult.live_pages} live · {autoResult.cached_pages} cached ·
                {' '}book now has <strong>{autoResult.total_contacts}</strong> total
                {autoResult.invite_sent_ok != null && (
                  <span> · WhatsApp sent <strong>{autoResult.invite_sent_ok}</strong>, failed <strong>{autoResult.invite_failed}</strong></span>
                )}
              </div>
            </div>
            <div className="border border-[#E8E1D5] rounded max-h-[300px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-sand-100 text-[10px] uppercase tracking-wider text-text-secondary sticky top-0">
                  <tr>
                    <th className="p-2 text-left">Profession</th>
                    <th className="p-2 text-left">Locality</th>
                    <th className="p-2 text-right">Results</th>
                    <th className="p-2 text-right">New</th>
                    <th className="p-2 text-right">Merged</th>
                    <th className="p-2 text-center">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {autoResult.pair_logs.map((p, i) => (
                    <tr key={i} className="border-t border-[#E8E1D5]">
                      <td className="p-2 font-bold text-text-primary">{p.profession}</td>
                      <td className="p-2 text-text-secondary">{p.locality}</td>
                      <td className="p-2 text-right tabular-nums">{p.results}</td>
                      <td className="p-2 text-right tabular-nums text-green-700">{p.saved_new || 0}</td>
                      <td className="p-2 text-right tabular-nums text-amber-700">{p.updated_existing || 0}</td>
                      <td className="p-2 text-center">
                        {p.error ? (
                          <span className="text-[10px] text-red-600">err</span>
                        ) : (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${p.from_cache ? 'bg-stone-100 text-stone-600' : 'bg-blue-50 text-blue-700'}`}>
                            {p.from_cache ? 'cache' : 'live'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-text-secondary border-l-2 border-brand-primary/30 pl-2">
              All saved contacts are tagged with every matching profession + city + area you searched, deduped by phone.
              Visit <strong>Marketing → Contacts</strong> to fire bulk WhatsApp campaigns per segment chip.
            </p>
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="border-t border-[#E8E1D5] pt-4 space-y-3" data-testid="cf-results">
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-text-secondary">
              <div>
                <strong className="text-text-primary">{result.total_contacts}</strong> NEW business{result.total_contacts === 1 ? '' : 'es'} ·
                {' '}{result.live_pages} live call{result.live_pages === 1 ? '' : 's'} · {result.cached_pages} cached
                {result.already_in_book > 0 && (
                  <span className="ml-1.5 text-amber-700">
                    · skipped <strong>{result.already_in_book}</strong> already in Contact Book
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={toggleAll}
                  className="text-xs underline text-brand-primary"
                  data-testid="cf-toggle-all"
                >
                  {allSelected ? 'Unselect all' : 'Select all'}
                </button>
                <span className="text-text-secondary">|</span>
                <span data-testid="cf-selected-count">{selectedList.length} ticked</span>
              </div>
            </div>

            <div className="border border-[#E8E1D5] rounded max-h-[450px] overflow-y-auto">
              {result.aggregated.length === 0 ? (
                <p className="p-4 text-sm text-text-secondary text-center">No phone-bearing businesses found. Try refining the profession or expanding the area.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-sand-100 text-[10px] uppercase tracking-wider text-text-secondary sticky top-0">
                    <tr>
                      <th className="p-2 w-8"></th>
                      <th className="p-2 text-left">Business</th>
                      <th className="p-2 text-left">Phone</th>
                      <th className="p-2 text-left">Area</th>
                      <th className="p-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.aggregated.map((r) => (
                      <tr key={r.phone} className="border-t border-[#E8E1D5] hover:bg-sand-50" data-testid={`cf-row-${r.phone}`}>
                        <td className="p-2 align-top">
                          <input
                            type="checkbox"
                            checked={!!selected[r.phone]}
                            onChange={() => toggleOne(r)}
                            className="h-4 w-4 accent-brand-primary"
                            data-testid={`cf-check-${r.phone}`}
                          />
                        </td>
                        <td className="p-2 align-top">
                          <div className="font-bold text-text-primary leading-tight">{r.name}</div>
                          {r.address && (
                            <div className="text-[10px] text-text-secondary flex items-start gap-1 mt-0.5">
                              <MapPin size={10} className="mt-0.5 flex-shrink-0" /> {r.address}
                            </div>
                          )}
                        </td>
                        <td className="p-2 align-top font-mono text-xs">
                          <a href={`tel:+91${r.phone}`} className="text-brand-primary hover:underline flex items-center gap-1">
                            <Phone size={10} /> {r.phone}
                          </a>
                        </td>
                        <td className="p-2 align-top text-[10px] text-text-secondary">
                          {(r.localities || []).join(', ')}
                        </td>
                        <td className="p-2 align-top">
                          {r.maps_url && (
                            <a
                              href={r.maps_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-text-secondary hover:text-brand-primary"
                              title="Open in Google Maps"
                              data-testid={`cf-maps-${r.phone}`}
                            >
                              <ArrowSquareOut size={14} />
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 justify-end">
              <Button
                onClick={copyPhones}
                variant="outline"
                className="border-[#E8E1D5] rounded-none h-9"
                data-testid="cf-copy-btn"
              >
                Copy phone{selectedList.length === 1 ? '' : 's'}
              </Button>
              <Button
                onClick={saveSelected}
                disabled={saving || !selectedList.length}
                className="bg-stone-700 hover:bg-stone-800 text-white rounded-none h-9 disabled:opacity-50"
                data-testid="cf-save-btn"
              >
                <FloppyDisk size={14} className="mr-1" />
                {saving ? 'Saving…' : `Save ${selectedList.length || ''} to Contact Book`}
              </Button>
              <Button
                onClick={saveAndInviteAll}
                disabled={saving || !selectedList.length}
                className="bg-green-600 hover:bg-green-700 text-white rounded-none h-9 disabled:opacity-50"
                data-testid="cf-save-invite-btn"
              >
                <WhatsappLogo size={14} weight="fill" className="mr-1" />
                {saving ? 'Sending…' : `Save & WhatsApp ${selectedList.length || ''}`}
              </Button>
            </div>

            <p className="text-[11px] text-text-secondary border-l-2 border-brand-primary/30 pl-2 flex items-start gap-1">
              <Database size={14} className="flex-shrink-0 mt-[1px]" />
              <span>
                Saved contacts appear in <strong>Marketing → Contact Book</strong>, tagged with the
                profession + cities + areas you searched. Phone numbers are <strong>unique</strong> — saving the
                same number twice never creates a duplicate, it just adds the new context to the existing row.
                Data is stored in MongoDB so it <strong>survives redeploys</strong>.
              </span>
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminContactFinderCard;
